// src/components/Control/robot/telemetry.ts
// Единый источник состояния робота для операторского UI: статус, автономия,
// поза, лидар, люди в кадре, препятствия камеры 2, состояние нод audio / vision.
// Подписки объявляются один раз на странице робота (useTelemetry в Control.tsx),
// компоненты читают нужные поля через useRobotState(селектор).
//   status          -> {status, chassis, link, lidar, firmware, camera, battery, charging, voltage,
//                       speed, angular, settings, cam2, map, slam, map_name, timestamp}
//                      chassis: false — Pi без шасси (камера, микрофон): ни езды, ни карты, ни лидара
//   mission         -> {mode, state, detail, front, person, goal, path, result}
//   pose / scan     -> только время прихода и скорость (рисует их MapViewer)
//   person          -> {width, height, people: [{id, known, box, fill, hands_up, ...}]}
//   people          -> {people: [{pid, name, created, seen, greeted}]}
//   cam2/obstacle   -> зоны препятствий камеры 2
//   audio/state, vision/state
//   pan_tilt/state  -> поворотная камера (нода pan_tilt): {ok, error, pan, tilt, target, released, config, asleep}
//   track/state     -> слежение камерой за объектом (нода tracker)
//   video/state     -> запись с камеры 1 (нода recorder)
//   snap/state      -> снимки с камер на роботе (нода snapshot)
//   laser/state     -> лазерная рулетка на поворотной камере (нода laser)
//   trainer/state   -> фитнес-тренер (нода trainer)
//   home/state      -> умный дом дворецкого: ленты WLED (нода home)
//   clients/**      -> liveliness-токены открытых страниц робота (useClientPresence в Control.tsx)
import { useRef } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store } from './store';
import { useZenohSubscription } from '../../../hooks/useZenohSubscription';
import { AudioState, VisionState, loadPref, savePref } from '../../../types/features';
import { Presence } from '../../../hooks/useClientPresence';
import type { SnapState } from './snapshots';

export type Mode = 'idle' | 'explore' | 'approach' | 'follow' | 'goto';

export const MODE_NAMES: Record<string, string> = {
  idle: 'ожидание', explore: 'объезд комнаты', approach: 'подъезд к человеку', follow: 'следование',
  goto: 'поездка к точке',
};

export interface Mission {
  mode: Mode;
  state?: string;
  detail?: string;
  front?: number | null;
  person?: { fill: number; cx: number; hands_up: boolean; name?: string | null } | null;
  goal?: { x: number; y: number } | null;
  path?: [number, number][] | null;
  result?: string;
}

export interface RobotSettings {
  autonomy?: { sources: { lidar: boolean; camera: boolean }; clearance: number; speed: number; wall: 'right' | 'left'; explore?: 'map' | 'wall' };
  person?: Record<string, unknown>;
  cams?: Record<'0' | '1', { stream: boolean; fps: number }>;
  cam2?: Record<string, unknown>;
  stop_on_obstacle?: boolean;
  lidar_stop?: boolean;
  maps?: { autosave_min: number; keep_auto: number };
  power?: PowerSettings;
}

// Поворотная камера (pan_tilt.py на роботе). Углы в градусах: pan + влево, tilt + вверх
export interface PanTiltConfig {
  pan_trim: number;
  tilt_trim: number;
  pan_invert: boolean;
  tilt_invert: boolean;
  pan_min: number;
  pan_max: number;
  tilt_min: number;
  tilt_max: number;
  speed_dps: number;
}

export interface PanTiltState {
  ok: boolean;
  error: string | null;
  pan: number;
  tilt: number;
  target: { pan: number; tilt: number };
  released: boolean;
  config: PanTiltConfig;
  asleep: boolean;
}

// Слежение за объектом (tracker_node.py). box — доли кадра камеры 1 [x0, y0, x1, y1]
export type TrackStatus = 'idle' | 'starting' | 'tracking' | 'lost' | 'stopped';
export interface TrackState {
  active: boolean;
  status: TrackStatus;
  box: number[] | null;
  since: number | null;
  lost_s: number;
  pan_tilt: boolean;      // камера может поворачиваться за объектом
  fps: number;
  algorithm: string;
  reason: string | null;  // почему слежение остановилось
  asleep: boolean;
}

// Лазерная рулетка TW10S на поворотной камере 1 (laser_node.py). Координаты — base_link
// робота в метрах: x вперёд, y влево, z вверх от пола; углы камеры в градусах (pan + влево)
export type LaserSide = 'left' | 'right';
export type LaserStatus = 'idle' | 'walls' | 'survey' | 'aiming' | 'done' | 'error' | 'stopped';
export interface LaserWallModel {
  kind?: 'flat' | 'curved';
  profile?: number[][];   // изогнутая стена: профиль от угла (x, y)
  sag?: number;
  m: number[];            // нормаль стены от робота
  rho: number;            // стена: m·(x, y) = rho
  corner: number[];       // угол с боковой стеной (x, y)
  e: number[];            // вдоль стены к углу
  z0: number;             // пол
  side: LaserSide;
  source: 'lidar' | 'survey';
  dist: number;
}
export interface LaserResult {
  target: { height: number; offset: number; side: LaserSide; by?: 'along' | 'straight' };
  point: number[];        // цель (x, y, z)
  pan: number;
  tilt: number;
  distance: number;       // от осей камеры до цели, м
  survey: { used: number; dropped: number; rms_mm: number | null; side: boolean; saved?: boolean } | null;
  wall?: { kind: 'flat' | 'curved'; sag_cm: number };
  loop?: { tries: number; residual_mm: number } | null;   // изогнутая стена: доводка по рулетке
  warnings?: string[];
  approach?: { dist: number; offset: number; side: LaserSide; short: boolean; seconds: number };  // подъезд по клику
  shift_cm: number;       // насколько обмер сдвинул цель против расчёта по лидару
  check: { measured: number | null; expected: number; diff_mm: number | null } | null;
  step_cm: number;        // шаг серво на этом расстоянии
  error_cm: number | null;
  seconds: number;
  model: LaserWallModel;
}
export interface LaserWalls {
  points?: number[][];    // точки скана (x, y), прорежены
  scans?: number;         // по скольким сканам (копятся, пока робот стоит)
  // по lines[i]: углы и через step м вдоль стены — где встанет робот [статус 0 негде/1 хорошо/2 не идеально, x, y, d, угол°]
  reach?: { corners: Record<LaserSide, number[] | null>; spots: number[][]; step: number }[];
  reach_height?: number;  // для какой высоты цели посчитано reach
  pivot_z?: number;       // высота осей кронштейна
  body_r?: number;        // радиус круга робота с запасом
  ignore?: number[][];    // отметки «не препятствие» [x, y, r] (base_link сейчас)
  lines: number[][];      // [x0, y0, x1, y1]
  front: { kind?: 'flat' | 'curved'; line: number[]; dist: number; angle_deg: number; sag_cm?: number | null;
           profile?: number[][] } | null;
  corners: Record<LaserSide, number[] | null>;
}
// Фитнес-тренер (trainer.py на роботе). Упражнения на повторы (kind "reps": присед, выпады) и на
// удержание (kind "hold": планка). Замеры повтора: присед (squat.py) сбоку thigh_deg (бедро к
// горизонтали, <0 — ниже параллели), knee_angle, lean_deg, weight_ahead (гиря впереди голеностопа,
// в длинах голени), heel_rise; спереди knee_ratio (колени / стопы), stance (стопы / плечи);
// выпад (lunge.py) front_knee, back_knee, shin_deg, lean_deg, front ("left" | "right");
// планка (plank.py) hips_pct (таз над линией плечо–голеностоп, % её длины), body_deg, view
export type TrainerPhase = 'idle' | 'waiting' | 'set' | 'rest' | 'done';
export type TrainerKind = 'reps' | 'hold';
export type TrainerView = 'side' | 'front' | 'oblique';
export type TrainerVoice = 'robot' | 'site' | 'both' | 'off';
export interface TrainerRep {
  n: number;
  set: number;
  view: TrainerView;
  depth: number;
  metrics: Record<string, number | string | null>;
  faults: string[];
  phrases: string[];
}
// Итог удержания (last у планки): секунды и замечания за всё удержание
export interface TrainerHold {
  set: number;
  hold_s: number;
  faults: string[];
  phrases: string[];
  metrics: Record<string, number | string | null>;
}
// Подход: на повторы — reps/good/faults (сколько раз), на удержание — hold_s/target_s/faults (секунды)
export interface TrainerSet {
  set: number;
  reps?: number;
  good?: number;
  hold_s?: number;
  target_s?: number;
  faults: Record<string, number>;
}
export interface TrainerConfig {
  exercise: string;
  voice: TrainerVoice;
  count: boolean;
  target_reps: number;
  hold_s: number;
  sets: number;
  rest_s: number;
  set_end_s: number;
}
export interface TrainerState {
  active: boolean;
  phase: TrainerPhase;
  exercise: string;
  kind?: TrainerKind;             // нет у старой версии ПО на Pi — повторы
  set: number;
  sets: number;
  reps: number;
  target_reps: number;
  hold?: number | null;           // удержание: секунд сейчас (null — не в планке)
  hold_s?: number;                // удержание: цель, с
  good: number;
  rest_left: number | null;
  ready: boolean;
  in_rep: boolean;
  view: TrainerView | null;
  seen: boolean;
  live?: { metrics: Record<string, number | string | null>; faults: string[] } | null;  // удержание сейчас
  last: TrainerRep | TrainerHold | null;
  history: TrainerSet[];
  config: TrainerConfig;
  exercises: Record<string, { name: string; kind?: TrainerKind; view: TrainerView; tips: string[] }>;
  timestamp: number;
}
export interface TrainerEvent {
  kind: 'start' | 'ready' | 'view' | 'rep' | 'hold' | 'fault' | 'fault_ok' | 'set' | 'rest' | 'done' | 'stop' | 'hint';
  text: string;
  say: string | null;
  level: 'info' | 'ok' | 'warn';
  rep?: TrainerRep;
  summary?: TrainerSet;
  view?: TrainerView;
}

// Умный дом дворецкого «Грач» (home.py на роботе): адресные ленты WLED и клавиши выключателей Tuya
export interface HomeDevice {
  id: string;
  kind?: 'wled' | 'tuya';  // нет — лента WLED; tuya — клавиша выключателя: только вкл/выкл
  group?: 'main' | 'strips';
  tuya_id?: string;
  channel?: number;       // клавиша выключателя
  version?: number;
  host: string;
  name: string;           // как её зовут голосом и на сайте ("" — по id)
  aliases: string[];      // ещё имена для голоса
  online: boolean | null; // null — ещё не спрашивали
  error: string | null;
  on: boolean | null;
  level: number | null;   // яркость, %
  rgb: [number, number, number] | null;
  effect: string | null;  // эффект WLED (английское имя)
  effects: string[];
  presets: { id: number; name: string }[];
  leds: number | null;
  ver: string | null;
  seen: number | null;
}
// Пресет дома: состояние всех устройств на момент сохранения
export interface HomeScene {
  id: string;
  name: string;
  devices: Record<string, { on: boolean; level?: number; rgb?: [number, number, number]; effect?: string }>;
  saved: number;
}
// Строка ежедневного расписания: в time включить пресет scene (или off — выключить всё) по дням days (0 — пн)
export interface HomeScheduleEntry {
  id: string;
  time: string;
  scene?: string;
  off?: boolean;
  days: number[];
  enabled: boolean;
  last: string | null;
}
export interface HomeState {
  devices: HomeDevice[];
  config: { transition_s: number };
  scenes?: HomeScene[];
  schedule?: HomeScheduleEntry[];
  timestamp: number;
}
export interface HomeEvent {
  type: 'result';
  ok: boolean;
  text: string;
  device: string | null;
  source: string;
  timestamp: number;
}

export interface LaserCal { pan_b: number; pan_k: number; tilt_b: number; tilt_k: number }
export interface LaserState {
  ok: boolean;            // рулетка на связи
  virtual?: boolean;      // модуля нет: показания — по стенам лидара, луч только рисуется на камере 1
  error: string | null;
  device: string | null;
  port: string;
  laser: boolean;         // луч горит
  busy: boolean;
  status: LaserStatus;
  progress: [number, number] | null;
  reason: string | null;
  result: LaserResult | null;
  measure: { distance: number | null; on_wall: { height: number; offset: number; off_wall: number; side: LaserSide;
                                                  by?: 'along' | 'straight' } | null; at: number } | null;
  walls: LaserWalls | null;
  cal: LaserCal;
  calibrated_at: number | null;
  moved: string | null;   // точка сбита: робот сдвинулся, камеру повернули
  walls_sens?: number;    // чувствительность поиска стен (0,5–3)
  pan_tilt: boolean;
  asleep: boolean;
}

// Запись с камеры 1 (recorder_node.py)
export interface VideoConfig {
  enabled: boolean;
  mode: 'continuous' | 'motion';
  fps: number;
  max_gb: number;
  max_days: number;       // 0 — без ограничения
  sensitivity: number;    // 1..10
  pre_s: number;
  post_s: number;
}
export type VideoStatus = 'off' | 'recording' | 'waiting' | 'no_camera' | 'asleep' | 'disk_full';
export interface VideoState {
  config: VideoConfig;
  status: VideoStatus;
  motion: number;         // доля изменившихся точек в последнем кадре
  used_bytes: number;
  free_bytes: number | null;
  oldest: number | null;
  newest: number | null;
  fps: number;
  error: string | null;
}

// Перезапуск и выключение (fleet_adapter: robot/<id>/system)
export type SystemCmd = 'restart_stack' | 'reboot' | 'poweroff';
export interface SystemState {
  pending: SystemCmd | null;
  error: string | null;
  can_power: boolean;     // установлен sudoers-fleetcore
  boot: number;           // время загрузки Pi, мс
}

// Режим сна (power.py на роботе)
export interface PowerSettings {
  sleep: boolean;
  auto_sleep: boolean;
  auto_sleep_min: number;
}

export interface PowerState {
  asleep: boolean;
  reason: 'manual' | 'no_clients' | null;
  since: number | null;
  clients: number;                 // страницы робота, которые видит сам робот
  users: Record<string, number>;
}

export interface StatusMsg {
  status?: string;
  source?: string;
  chassis?: boolean;     // false — Pi без шасси; нет поля (эмулятор, esp32-bridge) — шасси есть
  link?: string;
  lidar?: boolean;
  firmware?: string | null;
  camera?: boolean;
  battery?: number | null;
  charging?: boolean;
  voltage?: number | null;
  speed?: number;
  angular?: number;
  settings?: RobotSettings;
  cam2?: { online: boolean; fps?: number; firmware?: string; rssi?: number } | null;
  map?: boolean;
  map_name?: string | null;
  power?: PowerState;
  drive_block?: 'lidar' | 'cam2' | null;
  system?: SystemState;  // почему ручная команда «вперёд» не едет
  timestamp?: number;
}

export interface PersonDet {
  id?: number;
  held?: boolean;
  known?: { name: string; similarity: number } | null;
  fill: number;
  hands_up: boolean;
}

export interface KnownPerson {
  pid: string;
  name: string;
  created: number;
  seen: number | null;
  greeted: number | null;
}

export interface ObstacleMsg {
  obstacle: boolean;
  valid?: boolean;
  enabled?: boolean;
  source?: string;
  floor_fixed?: boolean;
  zones: Record<'left' | 'center' | 'right', { blocked: boolean; free: number }>;
}

export interface Telemetry {
  status: StatusMsg | null;
  statusAt: number;
  // Есть ли у робота шасси (колёса, лидар, карта). До первого статуса — как в прошлый раз
  // (chassisPref), чтобы страница Pi без шасси не мелькала картой и кнопками езды.
  chassis: boolean;
  mission: Mission | null;
  missionAt: number;
  poseAt: number;
  v: number;
  w: number;
  scanAt: number;
  scanHz: number;
  people: PersonDet[];
  personAt: number;
  known: KnownPerson[] | null;
  obstacle: ObstacleMsg | null;
  obstacleAt: number;
  audio: AudioState | null;
  vision: VisionState | null;
  clients: Presence;
  panTilt: PanTiltState | null;
  panTiltAt: number;
  track: TrackState | null;
  trackAt: number;
  video: VideoState | null;
  videoAt: number;
  snap: SnapState | null;
  snapAt: number;
  laser: LaserState | null;
  laserAt: number;
  trainer: TrainerState | null;
  trainerAt: number;
  home: HomeState | null;
  homeAt: number;
  // Последнее сообщение по каждому ключу — для «Диагностики» (текст обрезан)
  raw: Record<string, { at: number; text: string }>;
}

export const initialTelemetry: Telemetry = {
  status: null, statusAt: 0, chassis: true, mission: null, missionAt: 0, poseAt: 0, v: 0, w: 0, scanAt: 0, scanHz: 0,
  people: [], personAt: 0, known: null, obstacle: null, obstacleAt: 0, audio: null, vision: null,
  clients: { count: 0, users: {} }, panTilt: null, panTiltAt: 0,
  track: null, trackAt: 0, video: null, videoAt: 0, snap: null, snapAt: 0, laser: null, laserAt: 0,
  trainer: null, trainerAt: 0, home: null, homeAt: 0, raw: {},
};

export type TelemetryStore = Store<Telemetry>;

const chassisKey = (robotId: string) => `robot.${robotId}.chassis`;
export const chassisPref = (robotId: string) => loadPref(chassisKey(robotId), true);

// Сроки, после которых данные считаются устаревшими
export const STALE = { status: 5000, mission: 3000, pose: 3000, scan: 3000, person: 1500, obstacle: 2000, node: 6000 };

export const fresh = (at: number, ms: number) => at > 0 && Date.now() - at < ms;

// Поток кадров камер в «сырые» не попадает: бинарный
const RAW_MAX = 600;

function parse<T>(sample: zenoh.Sample): { msg: T; text: string } | null {
  const text = sample.payload().toString();
  try {
    return { msg: JSON.parse(text) as T, text };
  } catch {
    return null;
  }
}

export function useTelemetry(store: TelemetryStore, session: zenoh.Session | null, robotId: string) {
  const p = `robot/${robotId}`;
  const raw = (key: string, text: string) => {
    store.set(s => ({ raw: { ...s.raw, [key]: { at: Date.now(), text: text.length > RAW_MAX ? `${text.slice(0, RAW_MAX)}…` : text } } }));
  };
  // Частые потоки (поза, скан) обновляют хранилище не чаще THROTTLE_MS
  const THROTTLE_MS = 250;
  const throttle = (last: { at: number }, fn: () => void) => {
    const now = Date.now();
    if (now - last.at < THROTTLE_MS) return;
    last.at = now;
    fn();
  };

  useZenohSubscription(session, `${p}/status`, sample => {
    const r = parse<StatusMsg>(sample);
    if (!r) return;
    const chassis = r.msg.chassis !== false;
    if (chassis !== store.get().chassis) savePref(chassisKey(robotId), chassis);
    store.set({ status: r.msg, statusAt: Date.now(), chassis });
    raw('status', r.text);
  });
  useZenohSubscription(session, `${p}/mission`, sample => {
    const r = parse<Mission>(sample);
    if (!r) return;
    store.set({ mission: r.msg, missionAt: Date.now() });
    raw('mission', r.text);
  });
  const poseLast = useRefLike();
  useZenohSubscription(session, `${p}/pose`, sample => {
    throttle(poseLast, () => {
      const r = parse<{ v?: number; w?: number }>(sample);
      if (!r) return;
      store.set({ poseAt: Date.now(), v: r.msg.v ?? 0, w: r.msg.w ?? 0 });
      raw('pose', r.text);
    });
  });
  const scanLast = useRefLike();
  const arrivals = useRefLike<number[]>([]);
  useZenohSubscription(session, `${p}/scan`, () => {
    const now = Date.now();
    const a = arrivals.value;
    a.push(now);
    while (a.length && now - a[0] > 2000) a.shift();
    throttle(scanLast, () => store.set({ scanAt: now, scanHz: a.length / 2 }));
  });
  const personLast = useRefLike();
  useZenohSubscription(session, `${p}/person`, sample => {
    throttle(personLast, () => {
      const r = parse<{ people: PersonDet[] }>(sample);
      if (!r || !Array.isArray(r.msg.people)) return;
      store.set({ people: r.msg.people, personAt: Date.now() });
      raw('person', r.text);
    });
  });
  useZenohSubscription(session, `${p}/people`, sample => {
    const r = parse<{ people: KnownPerson[] }>(sample);
    if (!r) return;
    store.set({ known: r.msg.people ?? [] });
    raw('people', r.text);
  });
  const obsLast = useRefLike();
  useZenohSubscription(session, `${p}/cam2/obstacle`, sample => {
    throttle(obsLast, () => {
      const r = parse<ObstacleMsg>(sample);
      if (!r) return;
      store.set({ obstacle: r.msg, obstacleAt: Date.now() });
      raw('cam2/obstacle', r.text);
    });
  });
  useZenohSubscription(session, `${p}/audio/state`, sample => {
    const r = parse<AudioState>(sample);
    if (!r) return;
    store.set({ audio: { ...r.msg, timestamp: Date.now() } });
    raw('audio/state', r.text);
  });
  useZenohSubscription(session, `${p}/pan_tilt/state`, sample => {
    const r = parse<PanTiltState>(sample);
    if (!r) return;
    store.set({ panTilt: r.msg, panTiltAt: Date.now() });
    raw('pan_tilt/state', r.text);
  });
  const trackLast = useRefLike();
  useZenohSubscription(session, `${p}/track/state`, sample => {
    const r = parse<TrackState>(sample);
    if (!r) return;
    // Рамку двигаем на каждом кадре; «сырые» — не чаще THROTTLE_MS
    store.set({ track: r.msg, trackAt: Date.now() });
    throttle(trackLast, () => raw('track/state', r.text));
  });
  useZenohSubscription(session, `${p}/video/state`, sample => {
    const r = parse<VideoState>(sample);
    if (!r) return;
    store.set({ video: r.msg, videoAt: Date.now() });
    raw('video/state', r.text);
  });
  useZenohSubscription(session, `${p}/snap/state`, sample => {
    const r = parse<SnapState>(sample);
    if (!r) return;
    store.set({ snap: r.msg, snapAt: Date.now() });
    raw('snap/state', r.text);
  });
  useZenohSubscription(session, `${p}/laser/state`, sample => {
    const r = parse<LaserState>(sample);
    if (!r) return;
    store.set({ laser: r.msg, laserAt: Date.now() });
    raw('laser/state', r.text);
  });
  useZenohSubscription(session, `${p}/trainer/state`, sample => {
    const r = parse<TrainerState>(sample);
    if (!r) return;
    store.set({ trainer: r.msg, trainerAt: Date.now() });
    raw('trainer/state', r.text);
  });
  useZenohSubscription(session, `${p}/home/state`, sample => {
    const r = parse<HomeState>(sample);
    if (!r) return;
    store.set({ home: r.msg, homeAt: Date.now() });
    raw('home/state', r.text);
  });
  useZenohSubscription(session, `${p}/vision/state`, sample => {
    const r = parse<VisionState>(sample);
    if (!r) return;
    store.set({ vision: { ...r.msg, timestamp: Date.now() } });
    raw('vision/state', r.text);
  });
}

// Изменяемая ячейка на время жизни страницы: время последнего обновления и данные
function useRefLike<T = number>(initial?: T): { at: number; value: T } {
  const ref = useRef<{ at: number; value: T }>({ at: 0, value: initial as T });
  return ref.current;
}
